import { ThemeEvents } from '@theme/events';

/** @type {typeof window.requestIdleCallback} */
export const requestIdleCallback =
  typeof window.requestIdleCallback === 'function'
    ? window.requestIdleCallback.bind(window)
    : (callback) =>
      window.setTimeout(() => {
        callback(
            /** @type {IdleDeadline} */({
            didTimeout: false,
            timeRemaining: () => 0,
          })
        );
      }, 1);

/**
 * @param {() => void} callback
 */
export function onDocumentReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback);
  } else {
    callback();
  }
}

/**
 * @param {string} [type]
 * @param {{ headers?: Record<string, string>, body?: BodyInit | null }} [config]
 * @returns {RequestInit}
 */
export function fetchConfig(type = 'json', config = {}) {
  /** @type {Record<string, string>} */
  const headers = {
    'Content-Type': 'application/json',
    Accept: `application/${type}`,
    ...(config.headers || {}),
  };

  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest';
    delete headers['Content-Type'];
  }

  return {
    method: 'POST',
    headers,
    body: config.body,
  };
}

/**
 * @template {(...args: any[]) => void} T
 * @param {T} fn
 * @param {number} wait
 * @returns {T & { cancel: () => void }}
 */
export function debounce(fn, wait) {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timeout;

  /**
   * @param {...any} args
   */
  const debounced = function (...args) {
    if (timeout !== undefined) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };

  debounced.cancel = () => {
    if (timeout !== undefined) {
      clearTimeout(timeout);
    }
  };

  return /** @type {T & { cancel: () => void }} */ (debounced);
}

/**
 * @template {(...args: any[]) => void} T
 * @param {T} fn
 * @param {number} delay
 * @returns {T & { cancel: () => void }}
 */
export function throttle(fn, delay) {
  let lastCall = 0;

  /**
   * @param {...any} args
   */
  const throttled = function (...args) {
    const now = performance.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn.apply(this, args);
    }
  };

  throttled.cancel = () => {
    lastCall = performance.now();
  };

  return /** @type {T & { cancel: () => void }} */ (throttled);
}

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

export function prefersReducedMotion() {
  return reducedMotion.matches;
}

export const mediaQueryLarge = matchMedia('(min-width: 750px)');

export function isMobileBreakpoint() {
  return !mediaQueryLarge.matches;
}

/**
 * @param {Element | Element[]} elements
 * @param {(() => void) | undefined} callback
 * @param {GetAnimationsOptions} [options]
 */
export function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const list = Array.isArray(elements) ? elements : [elements];
  const animationPromises = list.flatMap((element) =>
    element
      .getAnimations(options)
      .filter((animation) => animation.timeline instanceof DocumentTimeline)
      .map((animation) => animation.finished)
  );

  return Promise.allSettled(animationPromises).then(() => {
    if (callback) callback();
  });
}

/**
 * @param {number} x
 * @param {number} y
 * @param {Element} element
 */
export function isPointWithinElement(x, y, element) {
  const { left, right, top, bottom } = element.getBoundingClientRect();
  return x >= left && x <= right && y >= top && y <= bottom;
}

/**
 * @param {MouseEvent} event
 * @param {Element} element
 */
export function isClickedOutside(event, element) {
  if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
    return !isPointWithinElement(event.clientX, event.clientY, element);
  }

  const eventPath = typeof event.composedPath === 'function' ? event.composedPath() : [];

  if (eventPath.includes(element)) {
    return false;
  }

  return !element.contains(event.target);
}

/**
 * @template {number | null} T
 * @param {string | number | null | undefined} value
 * @param {T} defaultValue
 * @returns {number | T}
 */
export function parseIntOrDefault(value, defaultValue) {
  if (value === null || value === undefined || value === '') return defaultValue;
  const parsed = parseInt(value.toString(), 10);
  return Number.isNaN(parsed) ? defaultValue : parsed;
}

/**
 * Writes the cart item count into every count badge on the page (the header bubble, and anything
 * else opting in with `data-cart-count`).
 *
 * Shared rather than owned by one component: the badge has to follow an add from any page, where
 * `cart-items-component` — which handles the quantity changes and removals of the cart page — is
 * not mounted at all.
 *
 * The value is mirrored on the attribute, not just in the text, so the CSS can hide an empty cart's
 * badge (`[data-cart-count="0"]`): no selector can match on text content.
 *
 * @param {number | undefined} itemCount
 */
export function updateCartCount(itemCount) {
  if (!Number.isFinite(itemCount)) return;

  const count = String(itemCount);

  document.querySelectorAll('[data-cart-count]').forEach((element) => {
    element.textContent = count;
    element.setAttribute('data-cart-count', count);
  });
}

/**
 * Whether a `variant-picker` element (or any element containing `[data-option-index]` radios)
 * has an explicit checked value for every one of its option groups.
 *
 * @param {Element} picker
 * @returns {boolean}
 */
export function isVariantPickerFullySelected(picker) {
  const optionIndexes = new Set(
    Array.from(picker.querySelectorAll('[data-option-index]')).map(
      (el) => /** @type {HTMLElement} */ (el).dataset.optionIndex
    )
  );
  // Scoped to [data-option-index]: bonnet fake-filter radios (bonnet-filter.js) must not count.
  const checkedIndexes = new Set(
    Array.from(picker.querySelectorAll('input[type="radio"][data-option-index]:checked')).map(
      (el) => /** @type {HTMLInputElement} */ (el).dataset.optionIndex
    )
  );
  return optionIndexes.size > 0 && optionIndexes.size === checkedIndexes.size;
}

/**
 * Opens the section's size drawer when its variant picker still lacks an explicit size choice,
 * so add-to-cart CTAs redirect to the size selection instead of submitting
 * (cf. .docs/product-page-integration.md décision 6).
 *
 * @param {Element} origin - Any element inside the product section (e.g. the clicked CTA).
 * @returns {boolean} Whether the drawer was opened — callers must then cancel their event.
 */
export function redirectToSizeDrawerIfUnselected(origin) {
  const sizeDrawer = origin.closest('.shopify-section')?.querySelector('size-drawer');
  if (!sizeDrawer || typeof (/** @type {any} */ (sizeDrawer).openDrawer) !== 'function') return false;

  // The size drawer holds a plain `div[ref="picker"]`, never a `variant-picker` (it has no
  // variants JSON — cf. .docs/product-page-integration.md, notes phase 2). Accept both so the
  // rule keeps working whichever markup the drawer ends up with.
  const picker = sizeDrawer.querySelector('variant-picker, [ref="picker"]');
  if (!picker || isVariantPickerFullySelected(picker)) return false;

  /** @type {any} */ (sizeDrawer).openDrawer();
  return true;
}

/**
 * The pending add of each CTA, as the controller that owns its listeners. One entry per CTA at most:
 * re-arming aborts the previous intent, so no amount of clicking can queue two adds, and a stale
 * intent (a color swap morphing the section under an open drawer) is simply replaced.
 * @type {WeakMap<Element, AbortController>}
 */
const pendingAdds = new WeakMap();

/**
 * Same redirection as `redirectToSizeDrawerIfUnselected`, plus the intent behind the click: the
 * drawer only opened because the customer asked to add to cart, so their size choice completes that
 * add instead of just selecting a variant. Opening the drawer by hand carries no intent — choosing a
 * size there selects it and nothing else — and dismissing the drawer drops the intent, so no add can
 * happen behind the customer's back.
 *
 * The replayed action is a click on the host's own `[ref="addToCartButton"]`, re-queried at replay
 * time: a color swap morphs the section and may have replaced the node in the meantime. The button
 * is still disabled for an unavailable variant (product-form.js), so choosing a sold-out size adds
 * nothing, exactly as a direct click would.
 *
 * @param {Element} origin - The add-to-cart host (buy-box or sticky bar).
 * @returns {boolean} Whether the drawer was opened — callers must then cancel their event.
 */
export function redirectAddToCartToSizeDrawer(origin) {
  if (!redirectToSizeDrawerIfUnselected(origin)) return false;

  const section = origin.closest('.shopify-section');
  const sizeDrawer = section?.querySelector('size-drawer');
  if (!section || !sizeDrawer) return true;

  pendingAdds.get(origin)?.abort();
  const controller = new AbortController();
  pendingAdds.set(origin, controller);

  // Dismissing the drawer without completing the choice drops the intent.
  sizeDrawer.addEventListener('dialog:close', () => controller.abort(), { signal: controller.signal });

  section.addEventListener(
    ThemeEvents.variantUpdate,
    (event) => {
      // No resource means an incomplete choice (or a combination with no variant): keep waiting.
      const variantId = /** @type {CustomEvent} */ (event).detail?.resource?.id;
      if (!variantId) return;

      // Consumed synchronously, so the `dialog:close` the choice itself triggers cannot drop it.
      controller.abort();

      // Replayed only once the drawer is gone, so the add-to-cart confirmation layer does not open
      // against a closing dialog. The product form has already taken the new variant id from this
      // very event, whatever the order the two listeners run in.
      sizeDrawer.addEventListener(
        'dialog:close',
        () => {
          const button = origin.querySelector('[ref="addToCartButton"]');
          if (button instanceof HTMLElement) button.click();
        },
        { once: true }
      );
    },
    { signal: controller.signal }
  );

  return true;
}

/**
 * Base64 of a URL, as Liquid's `base64_encode` writes it into an obfuscated link: the destination
 * must not show as a readable path in the HTML (OnCrawl recette of 13/10, slide 47).
 * Goes through UTF-8 bytes, since `btoa` only takes Latin-1.
 *
 * @param {string} url
 * @returns {string}
 */
export function encodeObfuscatedUrl(url) {
  return btoa(Array.from(new TextEncoder().encode(url), (byte) => String.fromCharCode(byte)).join(''));
}

/**
 * Reverse of `encodeObfuscatedUrl`. Returns `null` on a malformed value rather than throwing.
 *
 * @param {string | undefined} value
 * @returns {string | null}
 */
export function decodeObfuscatedUrl(value) {
  if (!value) return null;

  try {
    return new TextDecoder().decode(Uint8Array.from(atob(value), (char) => char.charCodeAt(0)));
  } catch {
    return null;
  }
}

class Scheduler {
  /**
   * @param {() => void} task
   */
  schedule(task) {
    requestAnimationFrame(() => setTimeout(task, 0));
  }
}

export const scheduler = new Scheduler();

if (typeof Theme !== 'undefined') {
  Theme.utilities = {
    ...Theme.utilities,
    scheduler,
  };
}
